import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeSlash, User, Plus, Phone, WhatsappLogo, CaretUp, CaretDown } from '@phosphor-icons/react';
import CustomClinikitActionButton from '../ui/CustomClinikitActionButton';
import EmptyDraw from '../ui/EmptyDraw';
import './PatientsTable.css';
import ContentLoader from '../shared/ContentLoader';

const STATUS_CONFIG = {
  active: { label: 'פעיל', color: 'green' },
  waiting: { label: 'בהמתנה', color: 'yellow' },
  finished: { label: 'סיים טיפול', color: 'gray' },
  initial: { label: 'פנייה ראשונית', color: 'blue' },
};

const STATUS_ORDER = { active: 0, waiting: 1, initial: 2, finished: 3 };

const getPhoneLinks = (phone) => {
  if (!phone) return null;
  const digits = phone.replace(/[^\d+]/g, "");
  const waDigits = digits.replace(/^\+/, "").replace(/^0/, "972").replace(/^9720/, "972");
  return { tel: `tel:${digits}`, whatsapp: `https://wa.me/${waDigits}` };
};

function PatientsTable({ 
  patients, 
  currentPage, 
  totalPages, 
  onPageChange, 
  isLoading,
  onRowClick,
  showTherapist = false,
  isAdminView = false,
  patientTypes = [],
  hasFilters = false,
  onAddPatient
}) {
  const navigate = useNavigate();
  const [visibleNotes, setVisibleNotes] = useState({});
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      // Date column reads most useful newest-first
      setSortDir(key === 'lastAppointment' ? 'desc' : 'asc');
    }
  };

  const sortedPatients = useMemo(() => {
    if (!sortKey || !patients) return patients;
    // Inactive patients sort into their own bucket after all statuses
    const statusRank = (p) =>
      p.isActive === false ? 98 : (STATUS_ORDER[p.status] ?? 99);
    const arr = [...patients];
    arr.sort((a, b) => {
      if (sortKey === 'lastAppointment') {
        const ta = a.lastAppointmentDate ? new Date(a.lastAppointmentDate).getTime() : null;
        const tb = b.lastAppointmentDate ? new Date(b.lastAppointmentDate).getTime() : null;
        // Patients without appointments always sort last
        if (ta === null && tb === null) return 0;
        if (ta === null) return 1;
        if (tb === null) return -1;
        return sortDir === 'asc' ? ta - tb : tb - ta;
      }
      const cmp =
        sortKey === 'name'
          ? `${a.firstName || ''} ${a.lastName || ''}`.localeCompare(
              `${b.firstName || ''} ${b.lastName || ''}`,
              'he'
            )
          : statusRank(a) - statusRank(b);
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return arr;
  }, [patients, sortKey, sortDir]);

  const renderSortIndicator = (key) => {
    if (sortKey !== key) return null;
    return sortDir === 'asc' ? (
      <CaretUp size={12} weight="bold" className="sort-indicator" />
    ) : (
      <CaretDown size={12} weight="bold" className="sort-indicator" />
    );
  };

  const ariaSort = (key) =>
    sortKey === key ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined;

  const toggleNoteVisibility = (patientId, e) => {
    e.stopPropagation();
    setVisibleNotes(prev => ({
      ...prev,
      [patientId]: !prev[patientId]
    }));
  };

  const handleRowClick = (patientId) => {
    if (onRowClick) {
      onRowClick(patientId);
    } else {
      navigate(`/dashboard/patients/${patientId}`);
    }
  };

  const formatDate = (date) => {
    if (!date) return '—';
    return new Date(date).toLocaleDateString('he-IL', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getInitials = (firstName, lastName) => {
    const first = firstName?.charAt(0) || '';
    const last = lastName?.charAt(0) || '';
    return `${first}${last}`;
  };

  // Get patient types for a patient
  const getPatientTypeItems = (patientTypeIds) => {
    if (!patientTypeIds?.length || !patientTypes?.length) return [];
    return patientTypeIds
      .map(id => patientTypes.find(t => t.id === id))
      .filter(Boolean);
  };

  // Check if we should show patient types column
  const showPatientTypesColumn = patientTypes?.length > 0;

  if (isLoading) {
    return (
      <ContentLoader fill message="טוען מטופלים..." />
    );
  }

  if (!patients || patients.length === 0) {
    return (
      <div className="patients-empty-state">
        <EmptyDraw variant={hasFilters ? "search" : "patients"} />
        <h3 className="empty-state-title">
          {hasFilters ? 'לא נמצאו תוצאות' : 'עוד אין מטופלים'}
        </h3>
        <p className="empty-state-desc">
          {hasFilters 
            ? 'נסה לשנות את מילות החיפוש או הסינון' 
            : 'הוסף את המטופל הראשון שלך והתחל לנהל את היומן'}
        </p>
        {!hasFilters && onAddPatient && (
          <CustomClinikitActionButton
            icon={<Plus size={16} weight="bold" />}
            onClick={onAddPatient}
          >
            הוסף מטופל ראשון
          </CustomClinikitActionButton>
        )}
      </div>
    );
  }

  return (
    <div className="patients-table-container">
      <div className="table-wrapper">
        <table className={`patients-table ${isAdminView ? 'admin-view' : ''}`}>
          <thead>
            <tr>
              <th scope="col" aria-sort={ariaSort('name')}>
                <button type="button" className="th-sort-btn" onClick={() => handleSort('name')}>
                  שם מלא
                  {renderSortIndicator('name')}
                </button>
              </th>
              {showPatientTypesColumn && <th scope="col">סוג מטופל</th>}
              {showTherapist && <th scope="col">מטפל/ת</th>}
              <th scope="col" className="th-phone">טלפון</th>
              <th scope="col" className="th-status" aria-sort={ariaSort('status')}>
                <button type="button" className="th-sort-btn" onClick={() => handleSort('status')}>
                  סטטוס
                  {renderSortIndicator('status')}
                </button>
              </th>
              <th scope="col">הערות</th>
              <th scope="col" aria-sort={ariaSort('lastAppointment')}>
                <button type="button" className="th-sort-btn" onClick={() => handleSort('lastAppointment')}>
                  פגישה אחרונה
                  {renderSortIndicator('lastAppointment')}
                </button>
              </th>
              <th scope="col" className="th-actions">פעולות</th>
            </tr>
          </thead>
          <tbody>
            {sortedPatients.map((patient, index) => {
              const status = STATUS_CONFIG[patient.status] || STATUS_CONFIG.active;
              const hasNotes = patient.adminNotes && patient.adminNotes.trim();
              const isNoteVisible = visibleNotes[patient.id];
              const fullName = `${patient.firstName} ${patient.lastName}`;
              const isEven = index % 2 === 1;

              return (
                <tr 
                  key={patient.id} 
                  onClick={() => handleRowClick(patient.id)}
                  className={`patient-row ${isEven ? 'row-even' : ''}`}
                >
                  <td className="patient-name-cell">
                    <div className="patient-info">
                      <div className="patient-avatar">
                        {getInitials(patient.firstName, patient.lastName)}
                      </div>
                      <span className="name-text">{fullName}</span>
                    </div>
                  </td>
                  {showPatientTypesColumn && (
                    <td className="patient-types-cell">
                      {getPatientTypeItems(patient.patientTypeIds).length > 0 ? (
                        <div className="patient-type-tags">
                          {getPatientTypeItems(patient.patientTypeIds).map(type => (
                            <span
                              key={type.id}
                              className="patient-type-tag"
                              style={{ 
                                backgroundColor: `${type.color}15`,
                                color: type.color
                              }}
                            >
                              {type.label}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="no-types">—</span>
                      )}
                    </td>
                  )}
                  {showTherapist && (
                    <td className="therapist-cell">
                      <div className="therapist-info">
                        <User size={14} weight="regular" />
                        <span>{patient.therapistName || '—'}</span>
                      </div>
                    </td>
                  )}
                  <td className="phone-cell">
                    <span className="phone-text">{patient.phone || '—'}</span>
                  </td>
                  <td className="status-cell">
                    {patient.isActive === false ? (
                      <span className="status-badge status-inactive">
                        לא פעיל
                      </span>
                    ) : (
                      <span className={`status-badge status-${status.color}`}>
                        {status.label}
                      </span>
                    )}
                  </td>
                  <td className="notes-cell">
                    {hasNotes ? (
                      <div className="notes-wrapper">
                        <button
                          className="notes-toggle"
                          onClick={(e) => toggleNoteVisibility(patient.id, e)}
                          title={isNoteVisible ? 'הסתר הערה' : 'הצג הערה'}
                        >
                          {isNoteVisible ? (
                            <EyeSlash size={18} weight="regular" />
                          ) : (
                            <Eye size={18} weight="regular" />
                          )}
                        </button>
                        {isNoteVisible && (
                          <span className="notes-text">{patient.adminNotes}</span>
                        )}
                        {!isNoteVisible && (
                          <span className="notes-hidden">יש הערה</span>
                        )}
                      </div>
                    ) : (
                      <span className="no-notes">—</span>
                    )}
                  </td>
                  <td className="date-cell">
                    {patient.lastAppointmentDate
                      ? formatDate(patient.lastAppointmentDate)
                      : 'אין פגישות'}
                  </td>
                  <td className="actions-cell">
                    {(() => {
                      const links = getPhoneLinks(patient.phone);
                      if (!links) return <span className="no-notes">—</span>;
                      return (
                        <div className="quick-actions">
                          <a
                            href={links.tel}
                            className="quick-action-btn quick-action-call"
                            onClick={(e) => e.stopPropagation()}
                            aria-label={`חיוג אל ${fullName}`}
                            title="חיוג"
                          >
                            <Phone size={16} weight="regular" />
                          </a>
                          <a
                            href={links.whatsapp}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="quick-action-btn quick-action-whatsapp"
                            onClick={(e) => e.stopPropagation()}
                            aria-label={`וואטסאפ אל ${fullName}`}
                            title="שליחת וואטסאפ"
                          >
                            <WhatsappLogo size={16} weight="regular" />
                          </a>
                        </div>
                      );
                    })()}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="pagination">
          <button
            className="pagination-btn"
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            ‹
          </button>
          <span className="pagination-info">
            עמוד {currentPage} מתוך {totalPages}
          </span>
          <button
            className="pagination-btn"
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            ›
          </button>
        </div>
      )}
    </div>
  );
}

export default PatientsTable;
